/**
 * ImageMotion motion tiers / model catalogue client wrappers.
 * Admin endpoints & costs stay in admin-only RPCs / edge functions.
 */

import { supabase } from './supabase';
import type {
  AdminListModelsResponse,
  AdminListTiersResponse,
  AdminModelTest,
  AdminModelTestResult,
  AdminModelTestScores,
  AdminMotionModel,
  MotionAnalyticsResponse,
  MotionTierKey,
  PublicMotionTier,
  RpcResult,
  SaveTierDraftPayload,
  UpsertMotionModelPayload,
} from '../types/motion-tiers';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const maybe = error as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [maybe.message, maybe.details, maybe.hint].filter(Boolean);
    if (parts.length) return parts.join(' | ');
    if (maybe.code) return `${fallback} (${maybe.code})`;
  }
  return fallback;
}

async function getFunctionInvokeErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (!error || typeof error !== 'object') {
    return getErrorMessage(error, fallback);
  }

  const maybe = error as { context?: Response };
  if (maybe.context && typeof maybe.context.json === 'function') {
    try {
      const payload = (await maybe.context.clone().json()) as {
        error?: string;
        message?: string;
        details?: string;
      };
      const parts = [payload.error, payload.message, payload.details].filter(Boolean);
      if (parts.length) return parts.join(' | ');
    } catch {
      // fall through
    }
  }

  return getErrorMessage(error, fallback);
}

function asRpcResult(data: unknown, fallbackError: string): RpcResult {
  if (data && typeof data === 'object') {
    const row = data as RpcResult;
    if (typeof row.success === 'boolean') return row;
  }
  return { success: false, error: fallbackError };
}

function parseJsonArrayField(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function normalizeModel(raw: Record<string, unknown>): AdminMotionModel {
  const durations = parseJsonArrayField(raw.supported_durations);
  const resolutions = parseJsonArrayField(raw.supported_resolutions);
  const aspects = parseJsonArrayField(raw.supported_aspect_ratios);
  const defaultSettings = parseJsonArrayField(raw.default_settings);
  const inputSchema = parseJsonArrayField(raw.input_schema);

  return {
    id: String(raw.id),
    provider: String(raw.provider ?? 'fal.ai'),
    endpoint: String(raw.endpoint ?? ''),
    admin_name: String(raw.admin_name ?? ''),
    adapter_type: (raw.adapter_type as AdminMotionModel['adapter_type']) || 'kling_v2',
    supported_durations: Array.isArray(durations)
      ? durations.map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : [5],
    supported_resolutions: Array.isArray(resolutions) ? resolutions.map(String) : ['1080p'],
    supported_aspect_ratios: Array.isArray(aspects) ? aspects.map(String) : ['16:9', '9:16', '1:1'],
    supports_audio: Boolean(raw.supports_audio),
    supports_end_frame: Boolean(raw.supports_end_frame),
    estimated_cost_fixed_usd:
      raw.estimated_cost_fixed_usd == null ? null : Number(raw.estimated_cost_fixed_usd),
    estimated_cost_formula:
      raw.estimated_cost_formula == null ? null : String(raw.estimated_cost_formula),
    default_settings:
      defaultSettings && typeof defaultSettings === 'object' && !Array.isArray(defaultSettings)
        ? (defaultSettings as Record<string, unknown>)
        : {},
    input_schema:
      inputSchema && typeof inputSchema === 'object' && !Array.isArray(inputSchema)
        ? (inputSchema as Record<string, unknown>)
        : {},
    is_active: raw.is_active !== false,
    testing_only: Boolean(raw.testing_only),
    admin_notes: raw.admin_notes == null ? null : String(raw.admin_notes),
    created_at: raw.created_at == null ? undefined : String(raw.created_at),
    updated_at: raw.updated_at == null ? undefined : String(raw.updated_at),
  };
}

async function invokeAdminModelTest(
  body: Record<string, unknown>
): Promise<{ success: boolean; error?: string; data?: Record<string, unknown> }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    return { success: false, error: 'You must be logged in' };
  }

  const { data, error } = await supabase.functions.invoke('imagemotion-admin-model-test', {
    body,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) {
    return {
      success: false,
      error: await getFunctionInvokeErrorMessage(error, 'Model test request failed'),
    };
  }

  if (data && typeof data === 'object' && (data as { error?: string }).error && !(data as RpcResult).success) {
    return {
      success: false,
      error: String((data as { error?: string }).error),
      data: data as Record<string, unknown>,
    };
  }

  return {
    success: (data as RpcResult)?.success !== false,
    error: (data as RpcResult)?.error,
    data: (data ?? {}) as Record<string, unknown>,
  };
}

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export async function fetchPublicMotionTiers(): Promise<PublicMotionTier[]> {
  const { data, error } = await supabase.rpc('get_imagemotion_public_tiers');
  if (error) {
    console.error('fetchPublicMotionTiers:', error);
    throw new Error(getErrorMessage(error, 'Failed to load motion tiers'));
  }
  if (!Array.isArray(data)) return [];
  return data as PublicMotionTier[];
}

// ---------------------------------------------------------------------------
// Admin models / tiers
// ---------------------------------------------------------------------------

export async function adminListMotionModels(): Promise<AdminListModelsResponse> {
  const { data, error } = await supabase.rpc('admin_list_imagemotion_models');
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to list models') };
  }
  const result = asRpcResult(data, 'Failed to list models') as AdminListModelsResponse;
  if (!result.success) return result;
  const models = Array.isArray(result.models)
    ? (result.models as unknown as Record<string, unknown>[]).map(normalizeModel)
    : [];
  return { success: true, models };
}

export async function adminUpsertMotionModel(
  payload: UpsertMotionModelPayload
): Promise<RpcResult & { model?: AdminMotionModel; created?: boolean }> {
  const { data, error } = await supabase.rpc('admin_upsert_imagemotion_model', {
    p_payload: payload,
  });
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to save model') };
  }
  const result = asRpcResult(data, 'Failed to save model');
  if (!result.success) return result;
  const modelRaw = result.model as Record<string, unknown> | undefined;
  return {
    ...result,
    model: modelRaw ? normalizeModel(modelRaw) : undefined,
    created: Boolean(result.created),
  };
}

export async function adminListMotionTiers(): Promise<AdminListTiersResponse> {
  const { data, error } = await supabase.rpc('admin_list_imagemotion_tiers');
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to list tiers') };
  }
  return asRpcResult(data, 'Failed to list tiers') as AdminListTiersResponse;
}

export async function adminSaveTierDraft(
  tierId: string,
  payload: SaveTierDraftPayload
): Promise<RpcResult> {
  const { data, error } = await supabase.rpc('admin_save_imagemotion_tier_draft', {
    p_tier_id: tierId,
    p_payload: payload,
  });
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to save draft') };
  }
  return asRpcResult(data, 'Failed to save draft');
}

export async function adminPublishTierVersion(versionId: string): Promise<RpcResult> {
  const { data, error } = await supabase.rpc('admin_publish_imagemotion_tier_version', {
    p_version_id: versionId,
  });
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to publish tier') };
  }
  return asRpcResult(data, 'Failed to publish tier');
}

export async function adminRollbackTier(tierId: string): Promise<RpcResult> {
  const { data, error } = await supabase.rpc('admin_rollback_imagemotion_tier', {
    p_tier_id: tierId,
  });
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to rollback tier') };
  }
  return asRpcResult(data, 'Failed to rollback tier');
}

export async function adminReorderTiers(orderedIds: string[]): Promise<RpcResult> {
  const { data, error } = await supabase.rpc('admin_reorder_imagemotion_tiers', {
    p_ordered_ids: orderedIds,
  });
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to reorder tiers') };
  }
  return asRpcResult(data, 'Failed to reorder tiers');
}

export async function adminMotionAnalytics(
  from: string,
  to: string,
  tierId?: string | null,
  modelId?: string | null
): Promise<MotionAnalyticsResponse> {
  const { data, error } = await supabase.rpc('admin_imagemotion_motion_analytics', {
    p_from: from,
    p_to: to,
    p_tier_id: tierId ?? null,
    p_model_id: modelId ?? null,
  });
  if (error) {
    return { success: false, error: getErrorMessage(error, 'Failed to load analytics') };
  }
  return asRpcResult(data, 'Failed to load analytics') as MotionAnalyticsResponse;
}

// ---------------------------------------------------------------------------
// Admin model testing (edge function)
// ---------------------------------------------------------------------------

export async function uploadAdminTestImage(
  file: File,
  userId: string
): Promise<{ url: string; path: string }> {
  const ext = file.name.split('.').pop() || 'jpg';
  const storagePath = `admin-tests/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { data, error } = await supabase.storage
    .from('imagemotion-uploads')
    .upload(storagePath, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from('imagemotion-uploads')
    .createSignedUrl(data.path, 60 * 60 * 24 * 7);

  if (signError || !signedData?.signedUrl) {
    throw signError ?? new Error('Failed to create signed URL');
  }

  return { url: signedData.signedUrl, path: data.path };
}

export async function adminEstimateTestCost(modelIds: string[]): Promise<{
  success: boolean;
  error?: string;
  estimated_total_cost_usd?: number;
  per_model?: { model_id: string; estimated_cost_usd: number | null; admin_name?: string }[];
}> {
  const res = await invokeAdminModelTest({ action: 'estimate', modelIds });
  if (!res.success) return { success: false, error: res.error };
  return {
    success: true,
    estimated_total_cost_usd:
      typeof res.data?.estimated_total_cost_usd === 'number'
        ? res.data.estimated_total_cost_usd
        : typeof res.data?.totalEstimatedCostUsd === 'number'
          ? (res.data.totalEstimatedCostUsd as number)
          : undefined,
    per_model: (res.data?.per_model ?? res.data?.perModel ?? []) as {
      model_id: string;
      estimated_cost_usd: number | null;
      admin_name?: string;
    }[],
  };
}

export async function adminStartModelTest(params: {
  imageUrl: string;
  imagePath?: string | null;
  prompt: string;
  modelIds: string[];
  durationSeconds?: number;
  resolution?: string;
  aspectRatio?: string;
  confirmCost?: boolean;
}): Promise<{ success: boolean; error?: string; testId?: string; test?: AdminModelTest }> {
  const res = await invokeAdminModelTest({
    action: 'start',
    imageUrl: params.imageUrl,
    imagePath: params.imagePath ?? null,
    prompt: params.prompt,
    modelIds: params.modelIds,
    durationSeconds: params.durationSeconds ?? 5,
    resolution: params.resolution ?? '1080p',
    aspectRatio: params.aspectRatio ?? '16:9',
    confirmCost: params.confirmCost !== false,
  });
  if (!res.success) return { success: false, error: res.error };
  return {
    success: true,
    testId: (res.data?.testId ?? res.data?.test_id) as string | undefined,
    test: res.data?.test as AdminModelTest | undefined,
    ...res.data,
  };
}

export async function adminPollModelTest(testId: string): Promise<{
  success: boolean;
  error?: string;
  test?: AdminModelTest;
  results?: AdminModelTestResult[];
  status?: string;
}> {
  const res = await invokeAdminModelTest({ action: 'poll', testId });
  if (!res.success) return { success: false, error: res.error };

  // Prefer edge payload; fall back to direct table read (RLS admin).
  if (!res.data?.results && !res.data?.test) {
    const { data: testRow, error: testErr } = await supabase
      .from('imagemotion_model_tests')
      .select('*')
      .eq('id', testId)
      .maybeSingle();

    if (testErr) {
      return { success: false, error: getErrorMessage(testErr, 'Failed to poll test') };
    }

    const { data: resultRows, error: resultErr } = await supabase
      .from('imagemotion_model_test_results')
      .select('*')
      .eq('test_id', testId)
      .order('created_at', { ascending: true });

    if (resultErr) {
      return { success: false, error: getErrorMessage(resultErr, 'Failed to load test results') };
    }

    return {
      success: true,
      test: testRow as AdminModelTest,
      results: (resultRows ?? []) as AdminModelTestResult[],
      status: (testRow as AdminModelTest | null)?.status,
    };
  }

  return {
    success: true,
    test: res.data?.test as AdminModelTest | undefined,
    results: (res.data?.results as AdminModelTestResult[] | undefined) ?? undefined,
    status: (res.data?.status as string | undefined) ?? (res.data?.test as AdminModelTest | undefined)?.status,
  };
}

export async function adminScoreTestResult(
  resultId: string,
  scores: AdminModelTestScores,
  ratingNotes?: string | null
): Promise<RpcResult> {
  const res = await invokeAdminModelTest({
    action: 'score',
    resultId,
    scores,
    ratingNotes: ratingNotes ?? null,
  });
  if (!res.success) return { success: false, error: res.error };
  return { success: true, ...res.data };
}

export async function adminAssignModelToTier(
  modelId: string,
  tierKey: MotionTierKey
): Promise<RpcResult & { draft_version_id?: string }> {
  const res = await invokeAdminModelTest({
    action: 'assign_to_tier',
    modelId,
    tierKey,
  });
  if (!res.success) return { success: false, error: res.error };
  return {
    success: true,
    draft_version_id: (res.data?.draft_version_id ?? res.data?.draftVersionId) as string | undefined,
    ...res.data,
  };
}
